/**
 * HIS V4 · page_key ของ presence
 *
 * convention (ตรงกับ his/core/presence/keys.py):
 *   <division_id>/HISV4/<menu>/enc:<encounterId>
 * ตัวอย่าง 6788/HISV4/doctor/enc:10555
 *
 * "แยกเช็คต่อ encounter ต่อ menu" ตามที่ตกลง -> ทุกเมนูของ PatientPanelV4 ได้ key ของตัวเอง
 */

const SAFE_PAGE_KEY = /^[A-Za-z0-9/:._-]+$/;
export const PAGE_KEY_MAX_LENGTH = 160;
export const PRESENCE_APP = "HISV4";
export const ENCOUNTER_REF_TYPE = "enc";

/**
 * คืน null เมื่อประกอบ key ไม่ได้ (division/encounter ยังไม่พร้อม)
 * caller ต้อง treat null = "ยังไม่เปิดใช้งาน presence" (mock mode / ข้อมูลไม่ครบ)
 */
export function makeEncounterPageKey(
  divisionId: number | string | null | undefined,
  menu: string,
  encounterId: number | string | null | undefined,
): string | null {
  const division = Number(divisionId);
  if (!Number.isFinite(division) || division <= 0) return null;
  if (!menu) return null;

  const encounter = Number(encounterId);
  if (!Number.isFinite(encounter) || encounter <= 0) return null;

  const key = `${division}/${PRESENCE_APP}/${menu}/${ENCOUNTER_REF_TYPE}:${encounter}`;
  if (key.length > PAGE_KEY_MAX_LENGTH || !SAFE_PAGE_KEY.test(key)) return null;
  return key;
}

/** สำหรับ test/debug: แยก parts ของ page_key */
export function parsePageKey(pageKey: string): {
  divisionId?: number;
  app?: string;
  menu?: string;
  refType?: string;
  refId?: string;
} {
  const [division, app, menu, ref] = (pageKey || "").split("/");
  const [refType, refId] = (ref || "").split(":");
  return {
    divisionId: Number.isFinite(Number(division)) ? Number(division) : undefined,
    app,
    menu,
    refType: refType || undefined,
    refId: refId || undefined,
  };
}

/**
 * client id ต่อ 1 tab เท่านั้น (sessionStorage = tab ใหม่ได้ค่าใหม่ / รีเฟรชได้ค่าเดิม)
 * ใช้ร่วมกันหลาย instance เพื่อไม่ให้ sid เปลี่ยนตอน reload
 */
const CLIENT_ID_STORAGE_KEY = "ih-presence-client-id";

export function clientIdOf(storage: Storage | null = safeSessionStorage()): string {
  try {
    const stored = storage?.getItem(CLIENT_ID_STORAGE_KEY);
    if (stored && SAFE_PAGE_KEY.test(stored) && stored.length >= 8) return stored;
  } catch (error) {
    /* jsdom / private mode = access denied -> ใช้ค่าใหม่ */
  }

  const fresh = newClientId();
  try {
    storage?.setItem(CLIENT_ID_STORAGE_KEY, fresh);
  } catch (error) {
    /* เก็บไม่ได้ก็ไม่เป็นไร sid จะต่างตอน reload */
  }
  return fresh;
}

function newClientId(): string {
  const crypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (crypto && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch (error) {
    return null;
  }
}
